<template>
  <v-container fluid class="office-dashboard pa-0">
    <h2 class="subheading grey--text">Panel de Oficina</h2>
    <p class="grey--text text--darken-1 mb-4">Resumen general del fraccionamiento — {{ todayLabelLong }}</p>

    <!-- Resumen principal -->
    <v-row>
      <v-col cols="12" sm="6" md="4" lg="2" v-for="card in summaryCards" :key="card.label">
        <v-card class="elevation-2 stat-card">
          <v-card-text>
            <v-icon :color="card.color" class="mb-2" large>{{ card.icon }}</v-icon>
            <div class="stat-value">{{ card.value }}</div>
            <div class="stat-label grey--text">{{ card.label }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Morosidad -->
    <v-row class="mt-2">
      <v-col cols="12" md="7">
        <v-card class="elevation-2">
          <v-card-title>
            <v-icon color="red" left>mdi-alert-circle-outline</v-icon>
            Morosidad del fraccionamiento
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" sm="4">
                <div class="stat-value">{{ delinquencySummary.totalDelinquent }}</div>
                <div class="grey--text">Propiedades morosas</div>
              </v-col>
              <v-col cols="12" sm="4">
                <div class="stat-value">{{ formatCurrency(delinquencySummary.totalDebt) }}</div>
                <div class="grey--text">Monto adeudado</div>
              </v-col>
              <v-col cols="12" sm="4">
                <div class="stat-value">{{ delinquencySummary.percentage }}%</div>
                <div class="grey--text">Porcentaje de morosidad</div>
              </v-col>
            </v-row>
            <v-divider class="my-3"></v-divider>
            <div v-for="bucket in delinquencySummary.buckets" :key="bucket.label" class="mb-3">
              <div class="d-flex justify-space-between">
                <span>{{ bucket.label }}</span>
                <span class="grey--text">{{ bucket.count }} casas · {{ formatCurrency(bucket.amount) }}</span>
              </div>
              <v-progress-linear
                :value="(bucket.amount / delinquencySummary.totalDebt) * 100"
                :color="statusColor(bucket.label)"
                height="10"
                rounded
              ></v-progress-linear>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Alertas -->
      <v-col cols="12" md="5">
        <v-card class="elevation-2">
          <v-card-title>
            <v-icon color="primary" left>mdi-bell-alert-outline</v-icon>
            Alertas administrativas
          </v-card-title>
          <v-card-text>
            <v-alert
              v-for="(alert, index) in administrativeAlerts"
              :key="index"
              :type="alert.type"
              outlined
              dense
              class="mb-2"
            >
              <v-icon left small>{{ alert.icon }}</v-icon>
              {{ alert.text }}
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Pagos recientes -->
    <v-row class="mt-2">
      <v-col cols="12">
        <v-card class="elevation-2">
          <v-card-title>
            <v-icon color="primary" left>mdi-cash-multiple</v-icon>
            Pagos recientes
          </v-card-title>
          <v-data-table
            :headers="paymentHeaders"
            :items="recentPayments"
            :hide-default-footer="true"
            :no-data-text="'No hay registros'"
            class="elevation-0"
          >
            <template v-slot:item.amount="{ item }">
              {{ formatCurrency(item.amount) }}
            </template>
            <template v-slot:item.status="{ item }">
              <v-chip small :color="statusColor(item.status)" text-color="white">{{ item.status }}</v-chip>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import {
  propertySummary,
  delinquencySummary,
  recentPayments,
  administrativeAlerts,
  statusColor
} from './officeMockData'

export default {
  name: 'OfficeDashboard',
  data () {
    return {
      propertySummary,
      delinquencySummary,
      recentPayments,
      administrativeAlerts,
      paymentHeaders: [
        { text: 'Fecha', value: 'date' },
        { text: 'Casa', value: 'house' },
        { text: 'Residente', value: 'resident' },
        { text: 'Concepto', value: 'concept' },
        { text: 'Monto', value: 'amount' },
        { text: 'Método de pago', value: 'method' },
        { text: 'Estado', value: 'status' }
      ]
    }
  },
  computed: {
    todayLabelLong () {
      return new Date().toLocaleDateString('es-MX', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      })
    },
    summaryCards () {
      return [
        { label: 'Total de propiedades', value: this.propertySummary.total, icon: 'mdi-home-city-outline', color: 'primary' },
        { label: 'Al corriente', value: this.propertySummary.current, icon: 'mdi-home-check-outline', color: 'green' },
        { label: 'Con adeudo', value: this.propertySummary.overdue, icon: 'mdi-home-alert-outline', color: 'red' },
        { label: 'Pendiente de cobro', value: this.formatCurrency(this.propertySummary.totalPending), icon: 'mdi-cash-remove', color: 'orange' },
        { label: 'Recaudado en agosto', value: this.formatCurrency(this.propertySummary.collectedMonth), icon: 'mdi-cash-check', color: 'green' },
        { label: 'Pagos por validar', value: this.propertySummary.pendingToValidate, icon: 'mdi-file-clock-outline', color: 'primary' }
      ]
    }
  },
  methods: {
    statusColor,
    formatCurrency (value) {
      return '$' + value.toLocaleString('es-MX')
    }
  }
}
</script>

<style scoped>
.office-dashboard {
  height: 100%;
  overflow-y: auto;
}

.stat-card {
  text-align: center;
  height: 100%;
}

.stat-value {
  font-size: 1.6rem;
  font-weight: 700;
}

.stat-label {
  font-size: 0.85rem;
}
</style>
